import { PaymentConfigurationSchema } from "../model/PaymentConfiguration";
import {
  collection,
  deleteField,
  doc,
  type DocumentData,
  documentId,
  type DocumentReference,
  type DocumentSnapshot,
  getDocFromServer,
  query,
  type Query,
  setDoc,
  where,
} from "firebase/firestore";
import { FirestoreProvider } from "./FirestoreProvider";
import { restrictAccessToDrivingSchoolIds } from "../backoffice.access_control";
import { t } from "../model/types";
import type { z } from "zod";
import { CancellationPolicySchema } from "../model/CancellationPolicy";
import { PostalAddressSchema } from "../model/PostalAddress";
import { firestore } from "../firebase";
import { DateTime } from "luxon";
import { CustomizationFlagEnum } from "../model/customizationFlags";
import { normalizeStreet, safeErrorToString } from "../backoffice.utils";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import _keyBy from "lodash/keyBy";
import { UpdateParams, UpdateResult } from "react-admin";
import _isEqual from "lodash/isEqual";
import { updateApplied } from "../utils/updateApplied";
import { isUndefined } from "../utils/filters";
import { Vehicle } from "./vehiclesProvider";

export const DrivingSchoolDefaultsSchema = t.object({
  paidLessonCancellationPolicy: CancellationPolicySchema,
  theoryLessonCancellationPolicy: CancellationPolicySchema,
});

export function isDrivingSchool(record: any): record is DrivingSchool {
  return typeof record === "object" && "uid" in record && "stripeConnectedAccountId" in record;
}

type DrivingSchoolDefaults = z.infer<typeof DrivingSchoolDefaultsSchema>;

let _defaults: DrivingSchoolDefaults;

export async function init(defaults?: DrivingSchoolDefaults) {
  if (defaults) {
    _defaults = defaults;
    return;
  }
  const snapshot = await getDocFromServer(doc(firestore, "config/defaults"));
  _defaults = t.object({ DrivingSchool: DrivingSchoolDefaultsSchema }).parse(snapshot.data()).DrivingSchool;
}

const BranchSchema = t.object({
  uid: t.uid(),
  drivingSchoolUid: t.uid(),
  name: t.optional(t.string()).transform((val) => val ?? ""),
  paymentTerminalId: t.string().nullish(),
  postalAddress: PostalAddressSchema,
  isDefaultBranch: t.boolean().optional(),
  maxAmountOfStudents: t.number().int(),
  publicHolidays: t.optional(t.array(t.object({ date: t.number(), name: t.string() }))),
  vogelId: t.optional(t.string()),
});

// Firebase DrivingSchoolLabels
const DrivingSchoolLabelSchema = schemas.DrivingSchoolLabelDto.pick({
  id: true,
  name: true,
  description: true,
  color: true,
});
export type DrivingSchoolLabel = z.infer<typeof DrivingSchoolLabelSchema>;

export type Branch = z.infer<typeof BranchSchema>;

const OtherCalendarEventCategorySchema = schemas.OtherEventCategoryDto.pick({
  name: true,
  description: true,
  type: true,
  availableDurations: true,
  isPaid: true,
}).extend({ deleted: t.boolean().optional().default(false) });

export type OtherCalendarEventCategory = z.infer<typeof OtherCalendarEventCategorySchema> & { id: string };

const DrivingSchoolFirestoreDocumentDataSchema = t
  .object({
    branches: t.optional(t.record(BranchSchema)).transform((val) =>
      Object.values(val ?? {}).map((branch) => ({
        id: branch.uid,
        ...branch,
        postalAddress: {
          ...branch.postalAddress,
          normalizedStreet: normalizeStreet(branch.postalAddress.street),
        },
      })),
    ),
    vehicles: t.optional(t.record(t.string(), t.any())),
    labels: t.optional(t.record(t.uid(), DrivingSchoolLabelSchema)),
    otherCalendarEventCategories: t.record(t.string(), OtherCalendarEventCategorySchema),
    customizations: t
      .optional(t.array(CustomizationFlagEnum.catch(() => "unknown" as any)))
      .transform((val) => (val ?? []).filter((it) => it !== ("unknown" as any))),
    hubspotCompanyId: t.hubspotCompanyId(),
    instructorUids: t.optional(t.array(t.uid())).transform((val) => val ?? []),
    logoUrl: t.optional(t.string()),
    name: t.string(),
    paidLessonCancellationPolicy: t
      .optional(CancellationPolicySchema)
      .transform((val) => val ?? _defaults.paidLessonCancellationPolicy),
    postalAddress: PostalAddressSchema,
    profileUrl: t.optional(t.string()),
    referralContent: t.object({ text: t.string().nullish(), title: t.string().nullish() }).optional(),
    stampStorageRef: t.string().nullish(),
    stripeConnectedAccountId: t.string().nullish(),
    paymentConfiguration: PaymentConfigurationSchema.optional(),
    theoryLessonCancellationPolicy: t
      .optional(CancellationPolicySchema)
      .transform((val) => val ?? _defaults.theoryLessonCancellationPolicy),
    uid: t.string(),
    startDate: t.dateTime(),
    endDate: t.dateTime().nullish(),
    threadId: t.string().uuid().nullish(),
    minimumSimulatorUE: t.number().int().nullish().default(0),
  })
  .transform((drivingSchool) => {
    for (const branch of drivingSchool.branches) {
      if (!branch.name) {
        // Fall back to the city (if there is only one branch in the city),
        // or to the street (if all branches are in the same city)
        // or to the city and the street otherwise ...
        if (drivingSchool.branches.filter((it) => it.postalAddress.city === branch.postalAddress.city).length === 1) {
          branch.name = branch.postalAddress.city;
        } else if (drivingSchool.branches.every((it) => it.postalAddress.city === branch.postalAddress.city)) {
          branch.name = branch.postalAddress.street;
        } else {
          branch.name = `${branch.postalAddress.city}, ${branch.postalAddress.street}`;
        }
      }
    }
    (drivingSchool as any).branchesById = _keyBy(drivingSchool.branches, (branch) => branch.id);
    (drivingSchool as any).id = drivingSchool.uid;
    (drivingSchool as any).isActive =
      drivingSchool.startDate < DateTime.now() && (!drivingSchool.endDate || DateTime.now() < drivingSchool.endDate);
    (drivingSchool as any).defaultBranch =
      drivingSchool.branches.find((branch) => branch.isDefaultBranch) ?? drivingSchool.branches[0];
    (drivingSchool as any).vehicles = Object.entries(drivingSchool.vehicles ?? {}).map(([id, data]) => {
      try {
        const { availabilities, entitledInstructorsIds, ...rest } = data;
        return _VehicleFromFirestoreSchema.parse({
          // We inject some default values here which are not stored in Firestore but are required by `VehicleDto` ...
          adasIds: [],
          // `availabilities` are stored as a JSON string in Firestore,
          // because Firestore does not support nested arrays ...
          availabilities: availabilities ? JSON.parse(availabilities) : [],
          branchId: null,
          drivingLicenseClasses: [],
          // `VehicleDto` uses the property name `entitledInstructorIds`,
          // but in Firestore we store the property `entitledInstructorsIds` ...
          entitledInstructorIds: entitledInstructorsIds ?? [],
          gearType: null,
          maxDaysInAdvance: null,
          minHoursInAdvance: null,
          notes: "",
          plateNumber: "",
          possibleSimulatorDrivingLessonDurations: [],
          vogelId: null,
          ...rest,
          id,
          drivingSchoolId: drivingSchool.uid,
        });
      } catch (error) {
        throw new Error(`Failed to parse vehicle ${id}: ${JSON.stringify(data, null, 2)}\n${safeErrorToString(error)}`);
      }
    });
    return drivingSchool;
  });

export type DrivingSchool = Omit<z.infer<typeof DrivingSchoolFirestoreDocumentDataSchema>, "vehicles"> & {
  id: string;
  isActive: boolean;
  branchesById: Record<string, Branch>;
  defaultBranch: Branch;
  vehicles: Array<Vehicle>;
};

class DrivingSchoolsProvider extends FirestoreProvider<DrivingSchool> {
  querySingle(id: string): [DocumentReference, (record: DrivingSchool) => void] {
    if (restrictAccessToDrivingSchoolIds && !restrictAccessToDrivingSchoolIds.includes(id)) {
      throw new Error(`Access to driving school ${id} denied`);
    }
    return [doc(firestore, `/driving_schools/${id}`), () => {}];
  }

  queryAll(): DocumentReference | Query {
    if (restrictAccessToDrivingSchoolIds?.length === 1) {
      return doc(firestore, `/driving_schools/${restrictAccessToDrivingSchoolIds[0]}`);
    } else if (restrictAccessToDrivingSchoolIds) {
      return query(
        collection(firestore, `/driving_schools`),
        where(documentId(), "in", restrictAccessToDrivingSchoolIds),
      );
    } else {
      return collection(firestore, `/driving_schools`);
    }
  }

  fromFirestore(doc: DocumentSnapshot): DrivingSchool {
    const drivingSchool = DrivingSchoolFirestoreDocumentDataSchema.parse(doc.data()) as DrivingSchool;
    return drivingSchool;
  }

  toFirestore(updateData: Partial<DrivingSchool>): DocumentData {
    const { referralContent, logoUrl, profileUrl, customizations } = updateData;
    return {
      ...(customizations && { customizations }),
      referralContent: referralContent ? referralContent : deleteField(),
      ...(logoUrl && { logoUrl }),
      ...(profileUrl && { profileUrl }),
    };
  }

  async update(
    resource: string,
    { id, previousData, data }: UpdateParams<DrivingSchool>,
  ): Promise<UpdateResult<DrivingSchool>> {
    const delta = Object.fromEntries(
      Object.entries(data).filter(([k, v]) => !_isEqual(v, (previousData as any)[k])),
    ) as Partial<DrivingSchool>;
    const deltaKeys = new Set(Object.keys(delta));
    if (deltaKeys.size === 0) {
      return { data: previousData };
    }
    let unsubscribe: undefined | (() => void);
    const updateReceived = new Promise<void>((resolve) => {
      unsubscribe = this.onUpdate(previousData, (drivingSchool) => {
        if (updateApplied(delta, drivingSchool)) {
          if (unsubscribe) {
            unsubscribe();
            unsubscribe = undefined;
          }
          resolve();
        }
      });
    });
    try {
      const SERVER_PROPERTIES = ["minimumSimulatorUE"];
      if (SERVER_PROPERTIES.some((key) => key in delta)) {
        const serverClient = await getAuthenticatedServerClient();
        const updateDto = {
          ...(deltaKeys.has("minimumSimulatorUE") && { minimumSimulatorUE: delta.minimumSimulatorUE }),
        } satisfies z.infer<typeof schemas.UpdateDrivingSchoolDto>;
        await serverClient.updateDrivingSchool(updateDto, { params: { id } });
      }
      const firestoreUpdateData = this.toFirestore(
        Object.fromEntries(
          Object.entries(delta).filter(([key, value]) => !isUndefined(value) && !SERVER_PROPERTIES.includes(key)),
        ),
      );
      if (Object.keys(firestoreUpdateData).length > 0) {
        console.info(`Updating driving_schools/${id} ...`, firestoreUpdateData);
        await setDoc(doc(firestore, `driving_schools/${id}`), firestoreUpdateData, { merge: true });
      }
      await updateReceived;
      return super.getOne(resource, { id });
    } finally {
      try {
        unsubscribe?.();
      } catch {
        // Ignored.
      }
    }
  }
}

export const drivingSchoolsProvider = new DrivingSchoolsProvider("DrivingSchool", "driving_schools");

const _VehicleFromFirestoreSchema = t.object({
  ...schemas.VehicleDto.shape,
  createdAt: t.dateTime(),
  updatedAt: t.dateTime(),
  deletedAt: t.dateTime().nullish(),
});
